import {
  HTMLAttributes,
  PropsWithChildren,
  memo,
  useMemo,
  useState,
} from 'react';
import { useUpdateEffect } from 'react-use';

import Clubs from './Clubs';
import { IconCaptain } from './Icons';
import { IconInsured } from './Icons/IconInsured';
import InputCheckbox from './InputCheckbox';
import Tags from './Tags';

import { InputRadio } from '.';

import { useGetPlayerPositionById } from '@/hooks/usePlayer';
import { PropsWithClassName, TournamentStatus } from '@/types';
import { EnumTeamContext, TemplatePlayer } from '@/types/team';
import {
  cn,
  formatMillionBalance,
  formatNumberToLocale,
  addAditionalZeroIfDecimal,
  resolvePlayerPrice,
  isF5,
} from '@/utils';

const List = ({
  children,
  className,
}: PropsWithClassName<PropsWithChildren>) => {
  return (
    <ul className={cn('space-y-[1px]', className)} role="list">
      {children}
    </ul>
  );
};

type ListItemProps = HTMLAttributes<HTMLLIElement> &
  PropsWithClassName<PropsWithChildren>;

const ListItem = ({ children, className, ...rest }: ListItemProps) => {
  return (
    <li className={className} {...rest}>
      {children}
    </li>
  );
};

type TypeState = {
  isChecked: boolean;
  statePlayerId: number;
  isSubstitute: boolean;
};

type CommonProps = {
  context?: EnumTeamContext;
  disabled?: boolean;
  onClick?: () => void;
  onInputChanges?: ({
    isChecked,
    statePlayerId,
    isSubstitute,
  }: TypeState) => void;
  variant: 'default' | 'selectable';
  initialCheckState: boolean;
};

export type DefaultVariantPlayerList = PropsWithClassName<{
  tournamentStatus?: TournamentStatus;
  team: string;
  teamId: number;
  nameInput?: string;
  labelInput?: string;
  isSubstitute: boolean;
  player: TemplatePlayer;
  isRadio?: boolean;
  isCaptain?: boolean;
  isInsured?: boolean;
  checked?: boolean;
  radioName?: string;
}>;

type Props = DefaultVariantPlayerList & CommonProps;

const Item = memo((props: Props) => {
  const {
    team,
    tournamentStatus,
    onClick,
    onInputChanges,
    labelInput,
    initialCheckState,
    nameInput,
    className,
    isRadio,
    disabled,
    checked,
    variant,
    radioName,
    teamId,
    isSubstitute,
    player,
    isCaptain,
    isInsured,
    context = EnumTeamContext.Build11,
  } = props;

  const {
    pm: averagePoints,
    n: name,
    a: lastName,
    cot: price,
    id,
    p: positionId,
    dts: pickedTimes,
    dtsF5: pickedTimesF5,
  } = player;

  const totalPrice = useMemo(() => {
    return resolvePlayerPrice(price);
  }, [price]);

  const playerPositionLabel = useGetPlayerPositionById(positionId);
  const [state, setState] = useState<TypeState>({
    isChecked: initialCheckState,
    statePlayerId: id || 0,
    isSubstitute,
  });
  const selectable = variant === 'selectable';
  useUpdateEffect(() => {
    onInputChanges &&
      onInputChanges({
        isChecked: state.isChecked,
        statePlayerId: state.statePlayerId,
        isSubstitute,
      });
  }, [state.isChecked]);

  return (
    <div
      aria-disabled={disabled}
      className={cn(
        'flex justify-between items-start pl-[20px] pr-[23px] py-[16px] bg-white h-[90px]',
        className,
        {
          'bg-gdt-gray-7 cursor-not-allowed': disabled && !selectable,
          'cursor-not-allowed bg-white': disabled && selectable,
          'pl-[16px] lg:pr-[10px] lg:h-[83px] py-[10px] items-stretch':
            selectable,
        }
      )}
      role="button"
      onClick={() => {
        if (!disabled) {
          if (selectable) {
            setState({ ...state, isChecked: !state.isChecked });
          } else {
            onClick && onClick();
          }
        }
      }}
    >
      <section
        className={cn('flex items-start self-center gap-3', {
          'items-stretch self-baseline': selectable,
        })}
      >
        {!selectable ? (
          <div
            className={cn('w-[30px] flex items-center justify-center', {
              'opacity-50': disabled,
            })}
          >
            <Clubs clubId={teamId} playerId={player.id} type="SHIRT" />
          </div>
        ) : (
          <div
            className={cn('flex items-center justify-center', {
              'opacity-50': !state.isChecked && selectable && !isRadio,
            })}
          >
            {isRadio ? (
              <InputRadio.Input
                isChecked={checked || false}
                label={labelInput || ''}
                name={radioName || ''}
                value={state.statePlayerId}
                onChange={() => {
                  return undefined;
                }}
              />
            ) : (
              <InputCheckbox
                disabled={disabled}
                isChecked={state.isChecked}
                label={labelInput}
                name={nameInput || ''}
                onChange={() => {
                  return undefined;
                }}
              />
            )}
          </div>
        )}
        <div
          className={cn('space-y-[5px]', {
            'opacity-50': !state.isChecked && selectable && !isRadio,
          })}
        >
          <p className="text-xs font-bold text-gdt-gray flex items-center">
            {lastName}, {name}{' '}
            {isCaptain && (
              <IconCaptain className="ml-1 border-none bg-gdt-blue text-white" />
            )}
            {isInsured && (
              <IconInsured className="ml-1 border-none bg-gdt-blue text-white" />
            )}
          </p>
          <p className="text-3xs font-semibold text-gdt-gray-4">
            {playerPositionLabel}
            {playerPositionLabel && ' - '}
            <span className="uppercase">{team}</span>
          </p>
          <div className="flex items-center gap-2">
            <p className="text-2xs font-semibold text-gdt-gray-3">
              ${formatMillionBalance(totalPrice)}
            </p>
            {tournamentStatus && (
              <Tags.Alert
                isUppercase
                statusId={tournamentStatus.id}
                textClassName="text-3xs"
              >
                {tournamentStatus.nombre}
              </Tags.Alert>
            )}
          </div>
        </div>
      </section>
      <section
        className={cn('flex max-w-[100px] w-full', {
          'opacity-50': !state.isChecked && selectable && !isRadio,
          'lg:max-w-[150px] lg:pr-6': selectable,
          'lg:max-w-[180px]': isRadio,
        })}
      >
        <div
          className={cn('text-center flex-1', {
            'lg:min-w-[70px] lg:w-auto': selectable,
            'lg:min-w-[90px] lg:w-auto text-center': isRadio,
          })}
        >
          <p className="font-semibold text-xs text-gdt-gray-3">
            {formatNumberToLocale(
              context && isF5(context) ? pickedTimesF5 : pickedTimes
            )}
          </p>
        </div>
        <div
          className={cn('text-center flex-1', {
            'lg:text-right lg:min-w-[80px] lg:w-auto': selectable,
            'lg:min-w-[90px] lg:text-center': isRadio,
          })}
        >
          <p className="text-xs font-semibold text-gdt-gray-3">
            {addAditionalZeroIfDecimal(averagePoints || '-')}
          </p>
        </div>
      </section>
    </div>
  );
});

Item.displayName = 'Item';

const PlayerList = {
  Wrapper: List,
  ListItem,
  Player: Item,
};

export default PlayerList;
